"use client";

import clsx from "clsx";
import { forwardRef, useState } from "react";
import { DatePicker } from "../DatePicker";
import { Popover } from "../Popover";
import { Icon } from "../Icon";
import { formatFullDate, formatHours } from "format-dates";
import type {
  DPDatesMode,
  DPDatesConfig,
  DPTimeLimit,
} from "@rehookify/datepicker";

export type DateInputProps = {
  label?: string;
  description?: string;
  error?: string;
  block?: boolean;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  locale: MatchiLocale;
  timeZone?: string;
  type?: DPDatesMode;
  toggle?: DPDatesConfig["toggle"];
  minDate?: DPDatesConfig["minDate"];
  maxDate?: DPDatesConfig["maxDate"];
  limit?: DPDatesConfig["limit"];
  showTime?: boolean;
  timeInterval?: number;
  minTime?: DPTimeLimit;
  maxTime?: DPTimeLimit;
  hour12?: boolean;
  value?: Date | Date[];
  onChange?: (dates: Date[]) => void;
};

const styles = {
  base: "antialiased cursor-pointer inline-flex items-center justify-between bg-white disabled:bg-gray-400 font-semibold text-gray-1200 border border-default hover:border-heavy whitespace-nowrap outline-none focus:ring-2 focus:ring-theme-accent ring-offset-1 transition duration-150 ease-in-out",
  size: {
    sm: "h-9 px-3 rounded-md text-sm",
    md: "h-10 px-4 rounded-md",
    lg: "h-12 px-4 rounded-md",
  },
  block: "flex w-full",
  error: "ring-2 ring-signal-error",
};

export const DateInput = forwardRef<HTMLButtonElement, DateInputProps>(
  (
    {
      label,
      description,
      error,
      block,
      size = "md",
      disabled = false,
      placeholder = "Select date",
      name,
      id,
      locale,
      timeZone,
      type = "single",
      toggle = false,
      minDate,
      maxDate,
      limit,
      showTime,
      timeInterval,
      minTime,
      maxTime,
      hour12,
      value,
      onChange,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);

    const selectedDates = Array.isArray(value) ? value : value ? [value] : [];

    const handleDatesChange = (dates: Date[]) => {
      if (onChange) {
        onChange(dates);
      }

      // Close popover after selection for single date mode with exception for time picker
      if (type === "single" && dates.length > 0 && !showTime) {
        setOpen(false);
      }
    };

    const formatDate = (date: Date) => {
      const dateStr = formatFullDate({ date, locale, timeZone });
      if (showTime) {
        const timeStr = formatHours({ date, locale, timeZone });
        return `${dateStr}, ${timeStr}`;
      }
      return dateStr;
    };

    const displayValue = (() => {
      if (selectedDates.length === 0) return "";
      if (type === "single") {
        return <div className="truncate">{formatDate(selectedDates[0])}</div>;
      }

      if (type === "multiple") {
        const count = selectedDates.length - 1;
        return (
          <div className="truncate">
            {formatDate(selectedDates[0])}
            {count > 0 && `, +${count}`}
          </div>
        );
      }

      if (selectedDates.length === 1) {
        return (
          <div className="flex items-center gap-1 truncate">
            <div className="truncate">{formatDate(selectedDates[0])}</div>
          </div>
        );
      }

      return (
        <div className="flex items-center gap-1 truncate">
          <div className="truncate">{formatDate(selectedDates[0])}</div>
          <Icon
            icon="CaretRightSolid"
            className="flex-shrink-0 text-xs text-gray-1000"
          />
          <div className="truncate">
            {formatDate(selectedDates[selectedDates.length - 1])}
          </div>
        </div>
      );
    })();

    return (
      <div className={clsx([block && styles.block, "flex-col"])}>
        {label && (
          <div className="flex items-center justify-between mb-1">
            <label
              className="text-gray-1200 block text-sm font-medium"
              htmlFor={id ?? name}
            >
              {label}
            </label>
          </div>
        )}

        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <button
              ref={ref}
              id={id ?? name}
              type="button"
              disabled={disabled}
              className={clsx([
                styles.base,
                styles.size[size],
                error && styles.error,
                block && styles.block,
                !displayValue && "text-gray-1100",
              ])}
            >
              <Icon
                icon="Calendar04Solid"
                className="mr-2 text-gray-1100 flex-shrink-0"
              />
              <div className="flex-1 text-left truncate">
                {displayValue || (
                  <div className="text-gray-1100">{placeholder}</div>
                )}
              </div>
            </button>
          </Popover.Trigger>

          <Popover.Container align="start" sideOffset={4}>
            <Popover.Content>
              <DatePicker
                // Remount on day change so the picker's internal focusDate
                // reinitialises from the externally-controlled value (lets
                // callers override the time after a day click).
                key={selectedDates[0]?.toDateString() ?? "empty"}
                locale={locale}
                timeZone={timeZone}
                type={type}
                toggle={toggle}
                minDate={minDate}
                maxDate={maxDate}
                limit={limit}
                showTime={showTime}
                timeInterval={timeInterval}
                minTime={minTime}
                maxTime={maxTime}
                hour12={hour12}
                selectedDates={selectedDates}
                onDatesChange={handleDatesChange}
              />
            </Popover.Content>
          </Popover.Container>
        </Popover.Root>

        {error && (
          <span className="text-signal-error mt-0.5 block text-sm font-medium">
            {error}
          </span>
        )}
        {!error && description && (
          <span className="text-gray-1000 mt-0.5 block text-sm">
            {description}
          </span>
        )}
      </div>
    );
  },
);

DateInput.displayName = "DateInput";
